import React, { ReactNode, useState } from 'react'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps } from 'react-router-dom'
import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Menu,
  MenuItem,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { Add as AddIcon } from '@material-ui/icons'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

interface Action {
  title: string
  onClick: () => void
}

interface MenuItem {
  Icon: React.FC<any>
  actions: Action[]
}

interface BreadcrumbProps {
  title: string
  url: string
}

export interface DialogProps {
  open: boolean
  handleClose: () => void
  onSave: () => void
}

interface DialogInfo {
  title: string
  Dialog: React.FC<DialogProps>
}

interface DialogElementInfo {
  title: string
  open: () => void
}

interface ObjInfo {
  data: any
  isLoading: boolean
  isFetching: boolean
}

interface DetailPageProps {
  obj: ObjInfo
  title: string
  refresh: () => void
  breadcrumbs: BreadcrumbProps[]
  createDialog?: DialogInfo
  createDialogs?: DialogElementInfo[]
  menuItems?: MenuItem[]
  detailComponent: ReactNode
  secondaryComponent?: ReactNode
  tabs?: string[]
  children?: ReactNode
}

interface TabPanelProps {
  children?: React.ReactNode
  index: any
  value: any
}

const TabPanel = (props: TabPanelProps) => {
  const { children = [], value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  )
}

export const DetailPage = (props: DetailPageProps) => {
  const {
    obj,
    title,
    refresh,
    breadcrumbs,
    createDialog,
    createDialogs = [],
    menuItems,
    detailComponent,
    secondaryComponent,
    tabs,
    children,
  } = props
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [tab, setTab] = useState(0)

  const initial = menuItems ? menuItems.map(() => null) : []
  const [anchorEls, setAnchorEls] = useState<(null | HTMLElement)[]>(initial)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const openFunc = (index: number) => {
    return (event: React.MouseEvent<HTMLButtonElement>) => {
      anchorEls[index] = event.currentTarget
      setAnchorEls([...anchorEls])
    }
  }
  const closeFunc = (index: number) => {
    return () => {
      anchorEls[index] = null
      setAnchorEls([...anchorEls])
    }
  }

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue)
  }

  return (
    <>
      <Helmet title={title} />
      {createDialog && createDialogOpen && (
        <createDialog.Dialog
          open={createDialogOpen}
          handleClose={() => {
            setCreateDialogOpen(false)
          }}
          onSave={refresh}
        />
      )}
      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            {title}
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            {breadcrumbs.map((bc) => (
              <Link key={bc.title} component={NavLink} exact to={bc.url}>
                {bc.title}
              </Link>
            ))}
            <Typography>{!obj.isFetching && obj.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
        {menuItems &&
          menuItems.map((item: MenuItem, index) => {
            return (
              <Grid item key={index}>
                <IconButton color="primary" onClick={openFunc(index)}>
                  <item.Icon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEls[index]}
                  open={Boolean(anchorEls[index])}
                  onClose={closeFunc(index)}
                >
                  {item.actions.map((action) => (
                    <MenuItem
                      key={action.title}
                      onClick={() => {
                        closeFunc(index)()
                        action.onClick()
                      }}
                    >
                      {action.title}
                    </MenuItem>
                  ))}
                </Menu>
              </Grid>
            )
          })}
        {!!createDialog && (
          <Grid item>
            <div>
              <IconButton color="primary" onClick={handleClick}>
                <AddIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {createDialog && (
                  <MenuItem
                    key={title}
                    onClick={() => {
                      handleClose()
                      setCreateDialogOpen(true)
                    }}
                  >
                    {createDialog.title}
                  </MenuItem>
                )}
                {createDialogs.map((createDialog) => (
                  <MenuItem
                    key={createDialog.title}
                    onClick={createDialog.open}
                  >
                    {createDialog.title}
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </Grid>
        )}
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Grid container spacing={6}>
            <Grid item xs={12} lg={6}>
              {detailComponent}
            </Grid>
            {secondaryComponent && (
              <Grid item xs={12} lg={6}>
                {secondaryComponent}
              </Grid>
            )}
          </Grid>
        </Grid>
        {tabs ? (
          <Grid container>
            <Grid item xs={12}>
              <Tabs value={tab} onChange={handleTabChange} aria-label="tabs">
                {tabs.map((label) => (
                  <Tab label={label} key={label} />
                ))}
              </Tabs>
            </Grid>
            <Grid item xs={12}>
              {Array.isArray(children) ? (
                <>
                  {children.map((child: ReactNode, index: number) => (
                    <TabPanel value={tab} index={index} key={index}>
                      {child}
                    </TabPanel>
                  ))}
                </>
              ) : (
                <TabPanel value={tab} index={0}>
                  {children}
                </TabPanel>
              )}
            </Grid>
          </Grid>
        ) : (
          <>
            {Array.isArray(children) ? (
              <>
                {children.map((child: ReactNode, index: number) => (
                  <Grid item xs={12} key={index}>
                    {child}
                  </Grid>
                ))}
              </>
            ) : (
              <Grid item xs={12}>
                {children}
              </Grid>
            )}
          </>
        )}
      </Grid>
    </>
  )
}
